/* ============================================================
   teg — logs.

   Two sources in one place: the files the backend tails, and the
   journals of the units it knows. Follow keeps the newest line in
   view; turning it off is how you read something that scrolled.
   ============================================================ */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from './lib';
import { api } from './api';

const FILES = [
  ['bot', 'pitvisor bot'], ['mtg-server', 'mtg server'], ['mtg-server-err', 'mtg errors'],
  ['muw-server', 'muw server'], ['muw-server-err', 'muw errors'],
  ['ojee-server', 'ojee server'], ['ojee-server-err', 'ojee errors'],
  ['nginx-error', 'nginx errors'], ['nginx-access', 'nginx access'],
];
const UNITS = [
  ['nginx', 'nginx'], ['mongod', 'mongodb'], ['tegtech', 'dashboard api'],
  ['factorio', 'factorio'], ['terraria', 'terraria'], ['terraria-api', 'terraria api'],
  ['mc-api', 'minecraft api'], ['cloudflared', 'cloudflare tunnel'],
];

export default function Logs({ token }) {
  const [source, setSource] = useState({ kind: 'unit', id: 'tegtech' });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [follow, setFollow] = useState(true);
  const [wrap, setWrap] = useState(false);
  const boxRef = useRef(null);
  // journalctl is not instant, and a poll for the previous source can land
  // after the one you just asked for. Every request carries a sequence number
  // and only the newest is allowed to write.
  const seq = useRef(0);

  const load = useCallback(async () => {
    const mine = (seq.current += 1);
    setBusy(true); setErr('');
    try {
      const d = source.kind === 'unit'
        ? await api.journal(token, source.id, 200)
        : await api.log(token, source.id);
      if (seq.current !== mine) return;
      setText(d.lines || '(nothing logged)');
    } catch (e) {
      if (seq.current !== mine) return;
      setErr(e.message); setText('');
    } finally { if (seq.current === mine) setBusy(false); }
  }, [token, source]);

  // Clearing on the way in, not on the way back: leaving the previous unit's
  // journal on screen while another one loads reads as "this is that unit's
  // log", and it can sit there for seconds.
  useEffect(() => { setText(''); setErr(''); }, [source]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!follow) return undefined;
    const id = setInterval(() => { if (!document.hidden) load(); }, 10000);
    return () => clearInterval(id);
  }, [follow, load]);

  useEffect(() => {
    if (follow && boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [text, follow]);

  const pick = (kind, id) => setSource({ kind, id });

  return (
    <div className="tg-view">
      <div className="tg-view-head">
        <h2 className="h2">Logs</h2>
        <div className="tg-log-tools">
          <button type="button" className={`btn btn--ghost btn--sm ${follow ? 'tg-on' : ''}`}
                  aria-pressed={follow} onClick={() => setFollow((v) => !v)}>
            <Icon name="activity" size={13}/> Follow
          </button>
          <button type="button" className={`btn btn--ghost btn--sm ${wrap ? 'tg-on' : ''}`}
                  aria-pressed={wrap} onClick={() => setWrap((v) => !v)}>Wrap</button>
          <button type="button" className="btn btn--ghost btn--sm" onClick={load} disabled={busy}>
            <Icon name="refresh" size={13}/> {busy ? '…' : 'Reload'}
          </button>
        </div>
      </div>

      <div className="tg-log-pick">
        <span className="label">Units</span>
        <div className="tg-log-chips">
          {UNITS.map(([id, label]) => (
            <button key={id} type="button"
                    className={`tg-chip ${source.kind === 'unit' && source.id === id ? 'on' : ''}`}
                    onClick={() => pick('unit', id)}>{label}</button>
          ))}
        </div>
        <span className="label">Files</span>
        <div className="tg-log-chips">
          {FILES.map(([id, label]) => (
            <button key={id} type="button"
                    className={`tg-chip ${source.kind === 'file' && source.id === id ? 'on' : ''}`}
                    onClick={() => pick('file', id)}>{label}</button>
          ))}
        </div>
      </div>

      {err && <div className="alert alert--err tg-banner">{err}</div>}
      {busy && !text ? (
        <div className="tg-log tg-log--big tg-log-loading" role="status" aria-live="polite">
          <span className="meta">Reading {source.kind === 'unit' ? 'journal' : 'file'} · {source.id}</span>
          {[92, 74, 86, 61, 80, 55, 88, 70].map((w, i) => (
            <span key={i} className="skeleton tg-log-skel" style={{ width: `${w}%` }}/>
          ))}
        </div>
      ) : (
        <pre className={`tg-log tg-log--big ${wrap ? 'is-wrap' : ''}`} ref={boxRef}>{text}</pre>
      )}
    </div>
  );
}
